import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
import { contactService, ContactInfoData } from '../services/firebase';

export interface ContactInfo {
  primaryPhone: string;
  secondaryPhone: string;
  email: string;
  address: string;
  businessHours: {
    weekdays: string;
    weekends: string;
    note: string;
  };
  mapsLink: string;
}

interface ContactContextType {
  contactInfo: ContactInfo;
  updateContactInfo: (info: ContactInfo) => Promise<void>;
  isLoading: boolean;
  isSaving: boolean;
}

const defaultContactInfo: ContactInfo = {
  primaryPhone: '+91 9424699693',
  secondaryPhone: '+91 6263930077',
  email: 'kariyasnursery@gmail.com',
  address: 'Umranala, Chhindwara, Madhya Pradesh, 480107',
  businessHours: {
    weekdays: 'Monday - Saturday: 8:00 AM - 6:00 PM',
    weekends: 'Sunday: 9:00 AM - 2:00 PM',
    note: 'Closed on major holidays'
  },
  mapsLink: 'https://maps.app.goo.gl/8ZYCGn5EsqtsrCrg8'
};

const CACHE_KEY = 'kariyasnursery_contact';

const ContactContext = createContext<ContactContextType | undefined>(undefined);

export const useContact = () => {
  const context = useContext(ContactContext);
  if (context === undefined) {
    throw new Error('useContact must be used within a ContactProvider');
  }
  return context;
};

// Helper to get cached data from localStorage
const getCachedContactInfo = (): ContactInfo => {
  try {
    const saved = localStorage.getItem(CACHE_KEY);
    if (saved) {
      const parsed = JSON.parse(saved);
      // Migrate old format if needed
      if (parsed.phone && !parsed.primaryPhone) {
        return {
          ...defaultContactInfo,
          ...parsed,
          primaryPhone: parsed.phone,
          secondaryPhone: parsed.secondaryPhone || defaultContactInfo.secondaryPhone
        };
      }
      return { ...defaultContactInfo, ...parsed };
    }
  } catch {
    // Return default if parsing fails
  }
  return defaultContactInfo;
};

// Helper to save to localStorage cache
const setCachedContactInfo = (info: ContactInfo) => {
  try {
    localStorage.setItem(CACHE_KEY, JSON.stringify(info));
  } catch (error) {
    console.error('Error caching contact info:', error);
  }
};

interface ContactProviderProps {
  children: React.ReactNode;
}

export const ContactProvider: React.FC<ContactProviderProps> = ({ children }) => {
  // Initialize from cache immediately for fast first render
  const [contactInfo, setContactInfo] = useState<ContactInfo>(getCachedContactInfo);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);

  // Fetch latest data from Firestore in background
  const fetchFromFirestore = useCallback(async () => {
    try {
      const firestoreData = await contactService.getContactInfo();

      if (firestoreData) {
        // Remove updatedAt field before setting state
        const { updatedAt, ...contactData } = firestoreData;
        setContactInfo(contactData);
        setCachedContactInfo(contactData);
      } else {
        // No data in Firestore yet, save default/cached data to Firestore
        const currentData = getCachedContactInfo();
        await contactService.saveContactInfo(currentData as ContactInfoData);
      }
    } catch (error) {
      console.error('Error fetching contact info from Firestore:', error);
      // Keep using cached data on error
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Fetch from Firestore on mount
  useEffect(() => {
    fetchFromFirestore();
  }, [fetchFromFirestore]);

  // Update contact info - saves to both Firestore and cache
  const updateContactInfo = async (info: ContactInfo) => {
    setIsSaving(true);
    try {
      // Update local state immediately for responsive UI
      setContactInfo(info);
      setCachedContactInfo(info);

      // Save to Firestore
      await contactService.saveContactInfo(info as ContactInfoData);
    } catch (error) {
      console.error('Error saving contact info:', error);
      // Revert to cached data on error
      const cachedData = getCachedContactInfo();
      setContactInfo(cachedData);
      throw error;
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <ContactContext.Provider value={{ contactInfo, updateContactInfo, isLoading, isSaving }}>
      {children}
    </ContactContext.Provider>
  );
};
